// @flow
import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
  BASE_URL,
  KAKAO_JAVASCRIPT_KEY,
  NAVER_CLIENT_ID,
  NAVER_REDIRECT_URI,
} from '@/config';
import { firebaseAuth } from '@/firebase';
import { Authority, ComsUserStatus, OAuthType } from '@/constants';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCustomToken,
  signInWithPopup,
} from 'firebase/auth';
import axios from 'axios';
import { authAtom } from '@/atoms/auth';
import { useRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { ComsUser } from '@/models/comsUser';
import { useNaverLogin } from '@/hooks/useNaverLogin';

type Props = { isTwoRow?: boolean };
export const SocialLogin = ({ isTwoRow = false }: Props) => {
  // const naverRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [auth, setAuth] = useRecoilState(authAtom);
  const { naverRef, naverLogin } = useNaverLogin();

  useEffect(() => {
    if (!Kakao.isInitialized()) {
      Kakao.init(KAKAO_JAVASCRIPT_KEY);
    }

    // const naver = (window as any).naver;
    //
    // const naverLogin: any = new naver.LoginWithNaverId({
    //   clientId: NAVER_CLIENT_ID,
    //   callbackUrl: NAVER_REDIRECT_URI,
    //   callbackHandle: true,
    //   isPopup: true,
    //   loginButton: {
    //     color: 'black',
    //     type: 1,
    //     height: 25,
    //   },
    // });
    //
    // naverLogin.init();
    // naverLogin.logout();
  }, []);

  (window as any).afterNaverLogin = (
    customToken: string,
    comsUser: ComsUser
  ) => {
    firebaseSignInWithCustomToken(customToken, comsUser);
  };

  const signInWithNaver = () => {
    // (naverRef.current?.children[0] as any)?.click();
    naverLogin();
  };

  const signInWithKakao = () => {
    Kakao.Auth.login({
      success: function (authObj: any) {
        console.log(authObj);
        // setKakaoIdToken(authObj.id_token);

        axios
          .post(
            `/user/kakao/oauth`,
            {
              accessToken: authObj.access_token,
              authority: Authority.ADMIN,
            },
            {
              baseURL: BASE_URL,
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            }
          )
          .then((response) => {
            const { customToken, comsUser } = response.data;
            firebaseSignInWithCustomToken(customToken, comsUser);
          });
      },
      fail: function (err) {
        alert(JSON.stringify(err));
      },
    });
  };

  const firebaseSignInWithCustomToken = (
    customToken: string,
    comsUser: ComsUser
  ) => {
    signInWithCustomToken(firebaseAuth, customToken)
      .then((userCredential) => {
        // Signed in
        const user = userCredential.user;
        handleAfterSignInWithComsServer(comsUser);
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        alert(errorMessage);
      });
  };

  const signInWithComsServer = (
    token: any,
    oauthType: OAuthType | null,
    authority: string
  ) => {
    return axios.post(
      `/user/firebase/oauth`,
      { oauthType, authority },
      {
        baseURL: BASE_URL,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const handleAfterSignInWithComsServer = (comsUser: ComsUser) => {
    const isAdmin = comsUser.appAuthorities
      .map((appAuthority) => appAuthority.authority)
      .includes(Authority.ADMIN);
    const isStaff = comsUser.appAuthorities
      .map((appAuthority) => appAuthority.authority)
      .includes(Authority.STAFF);
    if (
      (isAdmin || isStaff) &&
      comsUser.comsUserStatus === ComsUserStatus.INITIAL
    ) {
      navigate('/signUp', {
        state: {
          uid: comsUser.uid,
          email: comsUser.email,
          nameFromOauth: comsUser.nameFromOauth,
          phoneFromOauth: comsUser.phoneFromOauth,
        },
      });
    }
    if (!(isAdmin || isStaff)) {
      navigate('/unauthenticated');
    }
  };

  const signInWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(firebaseAuth, provider)
      .then((userCredential) => {
        // Signed in
        const user: any = userCredential.user;
        signInWithComsServer(
          user.accessToken,
          OAuthType.GOOGLE,
          Authority.ADMIN
        )
          .then((response) => {
            const comsUser = response.data;
            console.log(userCredential);
            console.log(user);
            comsUser.nameFromOauth = user.displayName;
            handleAfterSignInWithComsServer(comsUser);
          })
          .catch((error: any) => {
            const { errorCode, message: errorMessage } = error.response.data;
            alert(errorMessage);
          });
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        alert(errorMessage);
      });
  };

  const signInWithApple = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({
      locale: 'ko',
    });
    signInWithPopup(firebaseAuth, provider)
      .then((result) => {
        // The signed-in user info.
        const user: any = result.user;
        const displayName = user.displayName;

        // Apple credential
        const credential: any = OAuthProvider.credentialFromResult(result);
        const accessToken = credential.accessToken;
        const idToken = credential.idToken;

        console.log(user);
        console.log(credential);
        signInWithComsServer(user.accessToken, OAuthType.APPLE, Authority.ADMIN)
          .then((response) => {
            const comsUser = response.data;
            comsUser.nameFromOauth = user.displayName;
            handleAfterSignInWithComsServer(comsUser);
          })
          .catch((error: any) => {
            const { errorCode, message: errorMessage } = error.response.data;
            alert(errorMessage);
          });
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        alert(errorMessage);
      });
  };

  return (
    <div
      className={`flex ${
        isTwoRow ? 'flex-col gap-5' : 'flex-col sm:flex-row gap-4 sm:gap-8'
      } justify-center w-full`}>
      <div className="flex justify-center gap-8 w-full">
        <div ref={naverRef} style={{ display: 'none' }} id="naverIdLogin"></div>
        <button
          type="button"
          id="naverIdLogin"
          onClick={signInWithNaver}
          className="flex items-center justify-center border border-gray-700 hover:border-gray-900 hover:bg-gray-900 text-sm text-gray-300 p-3 rounded-lg tracking-wide font-medium cursor-pointer transition ease-in duration-500 px-">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
            <g transform="matrix(0.09375 0 0 0.09375 0 0)">
              <g>
                <path
                  d="M502.45398 256C 502.45398 324.0567 478.39233 382.1467 430.26898 430.27002C 382.14566 478.39337 324.05566 502.45505 255.999 502.45502C 187.94232 502.45505 129.85233 478.39337 81.72899 430.27002C 33.605656 382.1467 9.543991 324.0567 9.543991 256C 9.543991 187.94333 33.605656 129.85333 81.72899 81.729996C 129.85233 33.606663 187.94232 9.544998 255.999 9.544998C 324.05566 9.544998 382.14566 33.606663 430.26898 81.729996C 478.39233 129.85333 502.45398 187.94333 502.45398 256C 502.45398 258.86792 502.40396 261.735 502.30386 264.60117"
                  stroke="none"
                  fill="#2DB400"
                />
              </g>
              <g>
                <path
                  d="M124.152 139.41L215.89801 139.41C 215.89801 139.41 299.23 265.281 301.691 268.656C 304.211 272.066 304.50302 268.656 304.50302 268.656C 300.65903 249.179 296.48703 240.43701 296.48703 209.27701L296.48703 209.27701L296.48703 139.41L387.84604 139.41L387.84604 372.59L296.48703 372.59C 296.48703 372.59 214.86603 253.43399 212.40503 250.14099C 209.93202 246.825 209.60403 250.14099 209.60403 250.14099C 212.74504 266.219 215.52203 268.90298 215.52203 296.82898L215.52203 296.82898L215.52203 372.59097L124.15103 372.59097L124.15103 139.41L124.152 139.41z"
                  stroke="none"
                  fill="#FFFFFF"
                />
              </g>
            </g>
          </svg>
        </button>
        <button
          type="button"
          onClick={signInWithKakao}
          className="flex items-center justify-center border border-gray-700 hover:border-gray-900 hover:bg-gray-900 text-sm text-gray-300 p-3 rounded-lg tracking-wide font-medium cursor-pointer transition ease-in duration-500">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="36">
            <g transform="matrix(0.18755862 0 0 0.18755862 4.493904 -0)">
              <g>
                <path
                  d="M76.01 89.49L87.99 89.49L87.99 89.49L82 72.47L76.01 89.49L76.01 89.49"
                  stroke="none"
                  fill="#000000"
                />
                <path
                  d="M104 0C 46.56 0 0 36.71 0 82C 0 111.28 19.47 137 48.75 151.48001C 47.16 156.97002 38.510002 186.82 38.17 189.17001C 38.17 189.17001 37.96 190.93001 39.1 191.6C 39.870884 191.98883 40.767864 192.04309 41.579998 191.75C 44.859997 191.29 79.58 166.94 85.58 162.75C 91.68318 163.59752 97.83827 164.01521 104 164C 161.44 164 208 127.29 208 82C 208 36.71 161.44 0 104 0zM52.53 69.27C 52.399998 80.869995 52.629997 93.06999 52.44 104.49C 52.379997 108.14 50.28 109.229996 47.44 110.27C 47.119144 110.3834 46.773525 110.40759 46.44 110.34C 43.19 109.7 40.6 108.53999 40.519997 104.5C 40.289997 93.09 40.589996 80.87 40.429996 69.270004C 37.679996 69.16 33.759995 69.380005 31.209995 69.270004C 27.669994 69.04 25.209995 66.79 25.359995 63.440002C 25.509995 60.090004 27.299995 57.68 31.269995 57.620003C 40.649994 57.480003 52.269997 57.480003 61.649994 57.620003C 65.649994 57.680004 67.42999 60.100002 67.549995 63.440002C 67.67 66.78 65.24999 69.04 61.719994 69.270004C 59.2 69.38 55.29 69.16 52.53 69.27zM102.93 109.72C 101.73044 110.26594 100.427956 110.548935 99.11 110.55C 96.61 110.55 94.7 109.55 94.11 107.9L94.11 107.9L91.11 100.12L72.85 100.12L69.85 107.9C 69.27 109.53 67.36 110.55 64.85 110.55C 63.53515 110.54999 62.23571 110.26691 61.039997 109.72C 59.379997 108.96 57.789997 106.86 59.609997 101.2L59.609997 101.2L74 63.42C 75.23381 60.017647 78.3856 57.68532 82 57.5C 85.61037 57.705135 88.753914 60.035286 90 63.43L90 63.43L104.34 101.19C 106.17 106.86 104.58 109 102.93 109.72zM133.25 109.72L114 109.72C 110.89634 109.78149 108.32645 107.32333 108.25 104.22L108.25 104.22L108.25 63.5C 108.38283 60.213474 111.08579 57.617542 114.375 57.617542C 117.66421 57.617542 120.36717 60.213474 120.5 63.5L120.5 63.5L120.5 98.75L133.25 98.75C 135.29411 98.62668 137.23799 99.64717 138.29732 101.39972C 139.35664 103.15227 139.35664 105.34773 138.29732 107.10028C 137.23799 108.85283 135.29411 109.87332 133.25 109.75zM180.25 105.200005C 179.93066 107.62107 178.17584 109.60704 175.81253 110.22203C 173.4492 110.83702 170.94894 109.95831 169.49 108L169.49 108L155.42 89.37L153.34 91.450005L153.34 104.54001C 153.34 107.853714 150.6537 110.54001 147.34 110.54001C 144.02629 110.54001 141.34 107.853714 141.34 104.54001L141.34 104.54001L141.34 63.54001C 141.34 60.2263 144.02629 57.54001 147.34 57.54001C 150.6537 57.54001 153.34 60.2263 153.34 63.54001L153.34 63.54001L153.34 76.4L170.08 59.660004C 170.96527 58.78469 172.16518 58.30185 173.41 58.320004C 176.51262 58.401463 179.05582 60.806736 179.31 63.9C 179.419 65.248505 178.94164 66.57858 178 67.55L178 67.55L164.3 81.22L179.07 100.79C 180.02621 102.0593 180.43979 103.65611 180.22 105.23z"
                  stroke="none"
                  fill="#F7E600"
                />
              </g>
            </g>
          </svg>
        </button>
      </div>
      <div className="flex justify-center gap-8 w-full">
        <button
          type="button"
          onClick={signInWithGoogle}
          className="flex items-center justify-center border border-gray-700 hover:border-gray-900 hover:bg-gray-900 text-sm text-gray-300 p-3 rounded-lg tracking-wide font-medium cursor-pointer transition ease-in duration-500">
          <svg
            width="48px"
            height="48px"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#EA4335"
              d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z"
            />
            <path
              fill="#34A853"
              d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z"
            />
            <path
              fill="#4A90E2"
              d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z"
            />
            <path
              fill="#FBBC05"
              d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={signInWithApple}
          className="flex items-center justify-center border border-gray-700 hover:border-gray-900 hover:bg-gray-900 text-sm text-gray-300 p-3 rounded-lg tracking-wide font-medium cursor-pointer transition ease-in duration-500 px-">
          <svg
            fill="#FFFFFF"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="48px"
            height="48px">
            {' '}
            <path d="M 16.125 1 C 14.972 1.067 13.648328 1.7093438 12.861328 2.5273438 C 12.150328 3.2713438 11.589359 4.3763125 11.818359 5.4453125 C 13.071359 5.4783125 14.329031 4.8193281 15.082031 3.9863281 C 15.785031 3.2073281 16.318 2.12 16.125 1 z M 16.193359 5.4433594 C 14.384359 5.4433594 13.628 6.5546875 12.375 6.5546875 C 11.086 6.5546875 9.9076562 5.5136719 8.3476562 5.5136719 C 6.2256562 5.5146719 3 7.4803281 3 12.111328 C 3 16.324328 6.8176563 21 8.9726562 21 C 10.281656 21.013 10.599 20.176969 12.375 20.167969 C 14.153 20.154969 14.536656 21.011 15.847656 21 C 17.323656 20.989 18.476359 19.367031 19.318359 18.082031 C 19.922359 17.162031 20.170672 16.692344 20.638672 15.652344 C 17.165672 14.772344 16.474672 9.1716719 20.638672 8.0136719 C 19.852672 6.6726719 17.558359 5.4433594 16.193359 5.4433594 z" />
          </svg>
        </button>
      </div>
    </div>
  );
};
